/**
 * Task T: the plan / add-on catalogue rules shared by the API, the marketing website and the web app.
 *
 * Plans and add-ons are DATA (plan / addon tables, editable in Platform admin); this file only knows how to label the
 * feature keys and how to compare plans. A plan includes a feature when its features list has the key; an add-on
 * grants its feature_key plus extra_features. An add-on is "included" in a plan when the plan already has every
 * feature the add-on grants (e.g. the Website system add-on on the Full plan).
 */
export type CatalogLang = "en" | "zh";

export interface FeatureInfo { key: string; en: string; zh: string }

/** display order of the comparison table; unknown keys are shown after these, humanised */
export const FEATURE_CATALOG: FeatureInfo[] = [
  { key: "pos", en: "POS & checkout", zh: "收银与结账" },
  { key: "appointments", en: "Appointments & calendar", zh: "预约与日历" },
  { key: "customers", en: "Customers & CRM", zh: "顾客管理" },
  { key: "services", en: "Services, products & price list", zh: "服务、产品与价目表" },
  { key: "packages", en: "Packages, memberships & stored credit", zh: "配套、会员与储值" },
  { key: "subscriptions", en: "Subscriptions (recurring billing)", zh: "订阅（每月自动扣款）" },
  { key: "team", en: "Team, roles & commission views", zh: "团队、权限与提成查看" },
  { key: "attendance", en: "Attendance & phone clock-in (GPS)", zh: "考勤与员工手机打卡（GPS）" },
  { key: "staff_seats", en: "Unlimited team members", zh: "员工人数不限" },
  { key: "inventory", en: "Inventory & stock counts", zh: "库存与盘点" },
  { key: "whatsapp", en: "WhatsApp reminders & campaigns", zh: "WhatsApp 提醒与营销" },
  { key: "reports_basic", en: "Sales & daily close reports", zh: "销售与每日结账报表" },
  { key: "reports_advanced", en: "Advanced reports & export", zh: "高级报表与导出" },
  { key: "ai_assistant", en: "AI assistant & data import", zh: "AI 助手与数据导入" },
  { key: "website", en: "Professional website & online booking", zh: "专业品牌网站与在线预约" },
  { key: "shop", en: "Online shop", zh: "网店" },
  { key: "custom_domain", en: "Your own domain (e.g. aurasalon.com) with picture guide", zh: "使用自己的域名（如 aurasalon.com），附图文教程" },
  { key: "payroll", en: "Automatic payroll & commission", zh: "自动算薪与提成" },
  { key: "suppliers", en: "Suppliers, purchase orders & payables", zh: "供应商、采购单与应付账款" },
  { key: "multi_branch", en: "Multiple branches", zh: "多分店" },
  // (Task MM) storage blocks above the 5 GB every branch includes
  { key: "extra_storage", en: "Extra storage", zh: "额外存储空间" },
];

export function featureLabel(key: string, lang: CatalogLang): string {
  const f = FEATURE_CATALOG.find((x) => x.key === key);
  if (f) return f[lang];
  return key.replace(/_/g, " ").replace(/^./, (c) => c.toUpperCase());
}

/** feature keys in comparison-table order */
export function sortFeatures(keys: readonly string[]): string[] {
  const order = (k: string) => { const i = FEATURE_CATALOG.findIndex((f) => f.key === k); return i < 0 ? 999 : i; };
  return [...keys].sort((a, b) => order(a) - order(b) || a.localeCompare(b));
}

/**
 * (Task W, product owner 2026-10-03) features sold ONLY with the Full plan — never as a separate add-on on Base.
 * The apps show a "Available on the Full plan — upgrade" note where an owner would look for them.
 * (Task Y, 2026-10-03) "custom_domain": connecting the shop's own domain to its website.
 */
export const FULL_PLAN_ONLY_FEATURES = ["subscriptions", "attendance", "custom_domain"] as const;
export type FullPlanOnlyFeature = (typeof FULL_PLAN_ONLY_FEATURES)[number];
export const isFullPlanOnly = (key: string | undefined | null): key is FullPlanOnlyFeature => !!key && (FULL_PLAN_ONLY_FEATURES as readonly string[]).includes(key);

export interface CatalogI18nValue { zh?: { name?: string; description?: string } }
/** the localised name / description of a plan or add-on row (DB text is English; zh lives in i18n.zh) */
export function catalogText(row: { name: string; description?: string | null; i18n?: CatalogI18nValue | null }, lang: CatalogLang): { name: string; description: string } {
  const z = lang === "zh" ? row.i18n?.zh : undefined;
  return { name: z?.name || row.name, description: z?.description || row.description || "" };
}

export interface ComparePlan { code: string; features: string[] }
export interface CompareAddon { code: string; featureKey: string; extraFeatures?: string[]; priceMinor: number; currency: string; pricingModel: "flat" | "per_unit" | "tiered"; billingKind?: "licensed" | "metered"; unitLabel?: string | null; isActive?: boolean }

export const addonFeatureKeys = (a: Pick<CompareAddon, "featureKey" | "extraFeatures">): string[] => [a.featureKey, ...(a.extraFeatures ?? [])];
/** true when the plan already has everything the add-on grants */
export function addonIncludedIn(planFeatures: readonly string[], a: Pick<CompareAddon, "featureKey" | "extraFeatures">): boolean {
  return addonFeatureKeys(a).every((f) => planFeatures.includes(f));
}

export type CompareCell = { kind: "included" } | { kind: "addon"; addon: CompareAddon } | { kind: "none" };
export interface CompareRow { key: string; cells: CompareCell[] }

/**
 * Feature comparison table (rows = features, columns = plans): ✓ when the plan has it, the add-on (price) that
 * grants it when one is on sale, else none. Rows are the features any plan or on-sale add-on offers.
 */
export function compareRows(plans: ComparePlan[], addons: CompareAddon[]): CompareRow[] {
  const sale = addons.filter((a) => a.isActive !== false);
  const keys = new Set<string>();
  for (const p of plans) for (const f of p.features) keys.add(f);
  for (const a of sale) for (const f of addonFeatureKeys(a)) keys.add(f);
  const order = (k: string) => { const i = FEATURE_CATALOG.findIndex((f) => f.key === k); return i < 0 ? 999 : i; };
  return [...keys].sort((a, b) => order(a) - order(b) || a.localeCompare(b)).map((key) => ({
    key,
    cells: plans.map((p): CompareCell => {
      if (p.features.includes(key)) return { kind: "included" };
      const a = sale.find((x) => addonFeatureKeys(x).includes(key));
      return a ? { kind: "addon", addon: a } : { kind: "none" };
    }),
  }));
}

/** Monthly total of a plan plus the add-ons a salon would need to get the given features (for "Full saves you …") */
export function addonsTotalMinor(plan: ComparePlan, addons: CompareAddon[]): number {
  const seen = new Set<string>();
  let sum = 0;
  for (const a of addons) {
    if (a.isActive === false || a.pricingModel !== "flat" || a.billingKind === "metered" || addonIncludedIn(plan.features, a) || seen.has(a.code)) continue;
    seen.add(a.code); sum += a.priceMinor;
  }
  return sum;
}

/**
 * Effective plan limits = plan.limits + one per unit of every active quantity add-on with that limit_key
 * (e.g. Branches: limits.branches 1 + 2 extra branches = 3). Ready for the Branches add-on (not sold yet).
 */
export function effectiveLimitsOf(planLimits: Record<string, number>, addons: Array<{ limitKey: string | null; quantity: number }>): Record<string, number> {
  const out: Record<string, number> = { ...planLimits };
  for (const a of addons) if (a.limitKey) out[a.limitKey] = (out[a.limitKey] ?? 0) + Math.max(0, Math.floor(a.quantity));
  return out;
}
